import React from 'react';
import { observer } from 'mobx-react';
import './styles.css';
import Panel from '../Panel';
import IconButton from '../IconButton';
import { DoubleArrowRight, DoubleArrowLeft } from '../Icons';
import ObjectInspector from './ObjectInspector';
import DependencyInspector from './DependencyInspector';
import EventInspector from './EventInspector';
import MethodInspector from './MethodInspector';
import FSMInspector from './FSMInspector';
import StateInspector from './StateInspector';
import TransitionInspector from './TransitionInspector';
import InheritanceInspector from './InheritanceInspector';
import { useStores } from '../../hooks/useStores';
import type { IModel } from '../../stores/model';
import type { IMetaModel } from '../../stores/model/metamodel';
import type { IModelVersion } from '../../merode/controllers/Controller';
import type Report from '../../merode/checks/Report';

interface InspectorProps {
  /** The ModelVersion (passed to the controllers). */
  version: IModelVersion;
  /** The model. */
  model: IModel;
  /** Callback to close the inspector. */
  requestClose: () => void;
  /** Callback to open the inspector. */
  requestOpen: () => void;
  /** The id of the currently selected element. */
  selected: string;
  /** Callback to call when a Report is generated. */
  onReport?: (report: Report) => void;
  /** Callback to call when an FSM should be copied. */
  onCopyFSM: (objectTypeId: string, fsmId: string) => void;
  /** Callback to call when an element should be deleted. */
  onDelete?: (id: string) => void;
  /** Is the inspector visible or hidden? */
  visible: boolean;
}

/**
 * The inspector view
 *
 * Props:
 * - version: the ModelVersion
 * - model: the model
 * - requestClose: callback to close the inspector
 * - requestOpen: callback to open the inspector
 * - selected: the id of the currently selected element
 * - onReport: callback to call when a Report is generated
 * - onCopyFSM: callback to call when an FSM should be copied
 * - onDelete: callback to call when an element should be deleted
 * - visible: is the inspector visible or hidden?
 *
 * The Inspector store was injected with mobx-react (`inject(({ store }) => ({ ui:
 * store.ui.inspector }))`); it now comes from useStores().
 */
export default observer(function Inspector({
  version,
  model,
  requestClose,
  requestOpen,
  selected,
  onReport,
  onCopyFSM,
  onDelete,
  visible,
}: InspectorProps) {
  const store = useStores();
  const ui = store.ui.inspector;

  // `model.metamodel` reads as the raw MST instance type, whose `getXById`
  // accessors return raw instances rather than the hand-declared metamodel
  // interfaces the inspector props expect. View it through `IMetaModel` once so
  // every lookup below yields the concrete `IObjectType`/`IMethod`/... — same
  // node at runtime.
  const metamodel = model.metamodel as unknown as IMetaModel;

  // Find the selected element of each type (if any).
  const selectedObjectType = metamodel.getObjectTypeById(selected);
  const selectedDependency = metamodel.getDependencyById(selected);
  const selectedEventType = metamodel.getEventTypeById(selected);
  const selectedMethod = metamodel.getMethodById(selected);
  const selectedFSM = metamodel.getFSMById(selected);
  const selectedState = metamodel.getStateById(selected);
  const selectedTransition = metamodel.getTransitionById(selected);
  const selectedInheritance = metamodel.getInheritanceById(selected);

  // Render header in the open state
  const renderHeader = () => {
    return (
      <div className="inspector-view-header flex h-full flex-row items-center justify-center pl-[15px]">
        <div className="title flex-1">Inspector</div>
        <div className="close-wrapper basis-[37px] grow-0 shrink-0">
          <IconButton onClick={requestClose}>
            <DoubleArrowRight />
          </IconButton>
        </div>
      </div>
    );
  };

  // Render header in the collapse state
  const renderCollapsedHeader = () => {
    return (
      <div className="inspector-collapsed-header">
        <IconButton onClick={requestOpen} variant="narrow">
          <DoubleArrowLeft />
        </IconButton>
      </div>
    );
  };

  // Render an object inspector if there's an object type selected.
  //
  // The `key` remounts the inspector when the selected element changes (4.5
  // key-remount design): local form state (propagateRename, renameMethods…)
  // resets naturally with zero stale frame, and the inspector's discardChanges
  // cleanup effect runs on key change. Selecting a different element while the
  // inspector is dirty already discards via ProjectVersion.confirmable BEFORE
  // the new id lands here, so the fresh mount reads a clean `ui.changes`.
  const renderObjectInspector = () => {
    if (selectedObjectType) {
      return (
        <ObjectInspector
          key={selectedObjectType.id}
          version={version}
          object={selectedObjectType}
          ui={ui.objectInspector}
          onReport={onReport}
          onDelete={onDelete}
          requestClose={requestClose}
        />
      );
    }
  };

  // Render an event inspector if there's an event type selected
  const renderEventInspector = () => {
    if (selectedEventType) {
      return (
        <EventInspector
          key={selectedEventType.id}
          version={version}
          event={selectedEventType}
          ui={ui.eventInspector}
          onReport={onReport}
          requestClose={requestClose}
        />
      );
    }
  };

  // Render a dependency inspector if there's a dependency selected
  const renderDependencyInspector = () => {
    if (selectedDependency) {
      return (
        <DependencyInspector
          key={selectedDependency.id}
          version={version}
          dependency={selectedDependency}
          ui={ui.dependencyInspector}
          onReport={onReport}
          requestClose={requestClose}
        />
      );
    }
  };

  // Render a method inspector if there's a method selected
  const renderMethodInspector = () => {
    if (selectedMethod) {
      return (
        <MethodInspector
          version={version}
          method={selectedMethod}
          onReport={onReport}
          requestClose={requestClose}
          ui={ui.methodInspector}
        />
      );
    }
  };

  // Render an FSM inspector if there's an FSM selected
  const renderFSMInspector = () => {
    if (selectedFSM) {
      return (
        <FSMInspector
          version={version}
          fsm={selectedFSM}
          onReport={onReport}
          requestClose={requestClose}
          ui={ui.fsmInspector}
          onCopyFSM={onCopyFSM}
        />
      );
    }
  };

  // Render a state inspector if there's a state selected
  const renderStateInspector = () => {
    if (selectedState) {
      return (
        <StateInspector
          version={version}
          state={selectedState}
          onReport={onReport}
          requestClose={requestClose}
          ui={ui.stateInspector}
        />
      );
    }
  };

  // Render a transition inspector if there's a transition selected
  const renderTransitionInspector = () => {
    if (selectedTransition) {
      return (
        <TransitionInspector
          version={version}
          transition={selectedTransition}
          onReport={onReport}
          requestClose={requestClose}
          ui={ui.transitionInspector}
        />
      );
    }
  };

  // Render an inheritance inspector if there's an inheritance selected
  const renderInheritanceInspector = () => {
    if (selectedInheritance) {
      return (
        <InheritanceInspector
          version={version}
          inheritance={selectedInheritance}
          onReport={onReport}
          onDelete={onDelete}
          requestClose={requestClose}
        />
      );
    }
  };

  // Render a message when nothing is selected
  const renderEmpty = () => {
    if (!selected) {
      return <div className="inspector-empty p-[15px]">Nothing selected.</div>;
    }
  };

  return (
    <Panel
      className="inspector-view w-[300px] shrink-0 z-[9]"
      type="side"
      header={renderHeader}
      collapsed={!visible}
      collapsedHeader={renderCollapsedHeader}
    >
      {visible && (
        <div className="inspector-content h-full">
          {renderObjectInspector()}
          {renderEventInspector()}
          {renderDependencyInspector()}
          {renderMethodInspector()}
          {renderFSMInspector()}
          {renderStateInspector()}
          {renderTransitionInspector()}
          {renderInheritanceInspector()}
          {renderEmpty()}
        </div>
      )}
    </Panel>
  );
});
